import { useCallback, useEffect, useRef } from "react";
import { getSavedSpeakerDeviceId } from "../meetingPreferences";

export const notificationSoundPaths = {
  participantJoined: "sounds/calls/participant-joined.mp3",
  participantLeft: "sounds/calls/participant-left.mp3",
  messageSent: "sounds/chat/message-sent.mp3",
} as const;
export type NotificationSound = keyof typeof notificationSoundPaths;

export function createNotificationPlayer(event: NotificationSound) {
  const audio = new Audio(`${process.env.PUBLIC_URL || ""}/${notificationSoundPaths[event]}`);
  audio.preload = "none";
  audio.volume = 0.35;
  let generation = 0;
  return {
    async play() {
      const request = ++generation;
      try {
        if (event !== "messageSent") {
          const sink = (audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
          if (sink) await sink.call(audio, getSavedSpeakerDeviceId()).catch(() => undefined);
        }
        if (request !== generation) return;
        audio.currentTime = 0;
        await audio.play();
      } catch { /* Sound must never interrupt a call or queued message. */ }
    },
    dispose() {
      generation++;
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    },
  };
}

export function useNotificationSound(event: NotificationSound) {
  const player = useRef<ReturnType<typeof createNotificationPlayer> | null>(null);
  useEffect(() => {
    player.current = createNotificationPlayer(event);
    return () => { player.current?.dispose(); player.current = null; };
  }, [event]);
  return useCallback(() => { void player.current?.play(); }, []);
}
